/**
 * WorkOrderTab utility module — extracted in iter170 (component split
 * step #1) to reduce WorkOrderTab.jsx from 2,908 → ~2,810 LOC.
 *
 * Everything in this file is a PURE function or a static constant. No
 * component state, no closures over render-scoped variables. The
 * goal is to surface the lookups, formatters, and line-item math in a
 * single, testable location so future feature work can `import` them
 * without dragging the entire WorkOrderTab tree.
 */

// ── Status presentation ──
export const STATUS_LABELS = {
  draft: "Draft",
  submitted: "Submitted",
  approved: "Approved",
  on_hand: "On Hand",
  scheduled: "Scheduled",
  completed: "Completed",
  cancelled: "Cancelled",
};

export const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700",
  submitted: "bg-yellow-100 text-yellow-700",
  approved: "bg-blue-100 text-blue-700",
  on_hand: "bg-emerald-100 text-emerald-700",
  scheduled: "bg-purple-100 text-purple-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};

// iter220 — "On Hand" (material staging) inserted between approved and
// scheduled: draft → submitted → approved → on hand → scheduled →
// completed. The On Hand step's colour is driven by material receipt
// (see materialReceiptState): green = all received, yellow = partial.
export const WORKFLOW_STEPS = ["draft", "submitted", "approved", "on_hand", "scheduled", "completed"];

/**
 * iter220 — derive the WO's material-receipt state from its embedded
 * purchase_orders. Drives the "On Hand" workflow-step colour.
 *   "all"     → every material PO fully received  → green
 *   "partial" → some POs received / partially received → yellow
 *   "none"    → POs exist but nothing received yet
 *   null      → no purchase orders on this WO (nothing to stage)
 */
export const materialReceiptState = (purchaseOrders) => {
  const pos = (purchaseOrders || []).filter(
    (p) => (p.status || "").toLowerCase() !== "cancelled",
  );
  if (pos.length === 0) return null;
  const isReceived = (p) => (p.status || "").toLowerCase() === "received";
  const isPartial = (p) => {
    const s = (p.status || "").toLowerCase();
    if (s === "partial") return true;
    // Fallback: inspect line items when status vocabulary is stale.
    const lis = (p.line_items || []).filter((li) => (li.amount || 0) > 0);
    return lis.some((li) => li.received) && !lis.every((li) => li.received);
  };
  if (pos.every(isReceived)) return "all";
  if (pos.some((p) => isReceived(p) || isPartial(p))) return "partial";
  return "none";
};

// ── Cost-type taxonomy (matches Foundation GL codes) ──
export const COST_TYPES = [
  { value: "1", label: "Labor" },
  { value: "2", label: "Material" },
  { value: "3", label: "Subcontract" },
  { value: "4", label: "Freight" },
  { value: "5", label: "Equipment" },
  { value: "6", label: "Labor Burden" },
  { value: "7", label: "Other" },
];

// Legacy string → numeric mapping. Older line items used `"labor"`,
// `"material"`, etc.; the canonical export to Foundation is the
// numeric GL code. This map normalizes both inputs.
export const COST_TYPE_MAP = {
  labor: "1",
  material: "2",
  subcontract: "3",
  freight: "4",
  equipment: "5",
  travel: "7",
  disposal: "7",
};

export const costTypeLabel = (v) => {
  const ct =
    COST_TYPES.find((c) => c.value === v) ||
    COST_TYPES.find((c) => c.value === (COST_TYPE_MAP[v] || "7"));
  return ct ? `${ct.value}-${ct.label}` : v;
};

export const normalizeCostType = (v) => COST_TYPE_MAP[v] || v;

// ── Money helpers ──
export const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;

// ── Date helpers ──
/**
 * Add N business days (Mon-Fri) to a `YYYY-MM-DD` date string. N=0
 * returns the same date. Used by WO scheduling to advance off-Sundays
 * when computing default crew start dates.
 */
export const addBusinessDays = (yyyymmdd, n) => {
  if (!yyyymmdd || n <= 0) return yyyymmdd;
  const [y, m, d] = yyyymmdd.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  let added = 0;
  while (added < n) {
    dt.setDate(dt.getDate() + 1);
    const dow = dt.getDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

// ── Line item construction ──
export const emptyLineItem = (
  costType = "2",
  desc = "",
  qty = 1,
  price = 0,
  partNum = "",
  mfr = ""
) => ({
  id: Date.now().toString() + Math.random().toString(36).slice(2),
  description: desc,
  cost_type: COST_TYPE_MAP[costType] || costType,
  quantity: qty,
  unit_price: price,
  total: qty * price,
  base_quantity: qty,
  part_number: partNum,
  manufacturer: mfr,
});

// ── Description sanitization ──
/**
 * Strip internal annotations from a line-item description before
 * showing it to the customer:
 *   - Square-bracketed cost-type suffixes:  "Anchor [labor]" → "Anchor"
 *   - Parenthetical asides:  "Bolt (special order)" → "Bolt"
 *   - Self-referential duplications:  "Crown - Crown" → "Crown"
 *   - "Scope: Detail" prefix when the scope is redundant
 */
export const cleanDescription = (desc) => {
  let cleaned = (desc || "")
    .replace(/\s*\[.*?\]\s*/g, "")
    .replace(/\s*\(.*?\)\s*/g, "")
    .trim();
  // Handle "X - X" duplication
  const dashParts = cleaned.split(" - ");
  if (
    dashParts.length === 2 &&
    dashParts[0].trim().toLowerCase() === dashParts[1].trim().toLowerCase()
  ) {
    cleaned = dashParts[0].trim();
  }
  // Handle "Scope: Description" — remove scope prefix when it's redundant
  const colonIdx = cleaned.indexOf(": ");
  if (colonIdx > 0) {
    const scope = cleaned.substring(0, colonIdx).trim(); // eslint-disable-line no-unused-vars
    const detail = cleaned.substring(colonIdx + 2).trim();
    if (detail.length > 0) {
      cleaned = detail;
    }
  }
  return cleaned;
};

// ── Line-item consolidation ──
/**
 * Consolidate line items by part_number (or cleaned description) for
 * customer-facing rendering. Keeps the material quantity as the
 * display quantity (cost_type 2 = material) and rolls all linked
 * labor/freight lines into a single total per part.
 */
export const consolidateLineItems = (items) => {
  const grouped = {};
  const result = [];
  (items || []).forEach((item) => {
    const pn = (item.part_number || "").trim();
    const cleanDesc = cleanDescription(item.description);
    const lineTotal = (item.quantity || 0) * (item.unit_price || 0);
    const ct = String(item.cost_type || "").toLowerCase();
    // Group by part_number if available, otherwise by cleaned description
    const key = pn || cleanDesc;
    if (key) {
      if (grouped[key]) {
        grouped[key].total += lineTotal;
        // Use material quantity as the display quantity
        if (ct === "2" || ct === "material") {
          grouped[key].quantity = item.quantity || 1;
        }
      } else {
        // Default quantity to material qty, or 1 if this isn't the material row
        const qty = ct === "2" || ct === "material" ? (item.quantity || 1) : 1;
        grouped[key] = {
          ...item,
          description: cleanDesc,
          total: lineTotal,
          quantity: qty,
          part_number: pn,
        };
        result.push(grouped[key]);
      }
    } else {
      result.push({ ...item, description: cleanDesc, total: lineTotal });
    }
  });
  // Set unit_price = total / qty for consolidated rows
  Object.values(grouped).forEach((r) => {
    r.unit_price = r.quantity > 0 ? r.total / r.quantity : r.total;
  });
  return result;
};
